import type {
  AssemblyDocument,
  GlbHierarchyNode,
  ModelAssetNode,
} from '@puppycad/cad-core';
import type {
  AssemblyBranchNodeHoverReference,
  AssemblyBranchNodeHoverTarget,
} from './resolveAssemblyBranchNodeHoverTarget';
import { resolveAssemblyBranchNodeReferenceTarget } from './resolveAssemblyBranchNodeReferenceTarget';

/**
 * 运行期装配分支节点 hover 解析输入。
 */
export interface ResolveAssemblyBranchNodeRuntimeHoverTargetInput {
  /** 当前项目模型根节点。 */
  readonly root: ModelAssetNode;
  /** 当前项目根装配工作文档；缺失时无法从提交后的 root `.puppy` 反查直接子节点。 */
  readonly assemblyDocument: AssemblyDocument | null;
  /** 当前 OpenProject 根节点 key，用于把完整 `_assembly.json` direct child 转换为运行期 nodeKey。 */
  readonly rootNodeKey: string;
  /** 当前 root tab 的 GLB hierarchy；提交后重新打开时，离线包内部 selectionObjectId 只存在于这里。 */
  readonly hierarchy: GlbHierarchyNode | null;
  /** 从当前活动 WorkAssembly snapshot 按稳定 nodeKey 查询运行时组件。 */
  readonly resolveRuntimeBindingByNodeKey?: (
    nodeKey: string,
  ) => PuppyCAD.ProjectModelRuntimeBinding | null;
  /** 左侧分支节点解码出的业务引用。 */
  readonly reference: AssemblyBranchNodeHoverReference;
}

/**
 * 通过统一装配节点引用识别结果创建 hover 目标。
 *
 * 设计说明：
 * hover 与切换版本都必须先做同一件事：把左侧业务引用识别成当前装配中的 direct child。
 * 这里仅保留 hover 所需的运行期对象 ID 提取，避免后续再出现 hover 能识别、切换版本不能识别的分叉。
 *
 * @param input 解析输入。
 * @returns Viewer 可消费的 hover 目标；无法可靠定位时返回 null。
 */
export function resolveAssemblyBranchNodeRuntimeHoverTarget(
  input: ResolveAssemblyBranchNodeRuntimeHoverTargetInput,
): AssemblyBranchNodeHoverTarget | null {
  const target = resolveAssemblyBranchNodeReferenceTarget(input);
  const objectId = target?.hierarchyNode?.objectId ?? target?.objectId;
  if (!target || !objectId) {
    return null;
  }

  return {
    nodeKey: target.nodeKey,
    objectId,
    nodeType: target.nodeType,
  };
}
